import {ChevronRight, ExpandLess, ExpandMore, KeyOutlined, MoreVert, Search} from '@mui/icons-material';
import {
    Box,
    Chip,
    Collapse,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    OutlinedInput,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {
    useGetPermissionCatalogQuery,
    useGetRolesQuery,
} from 'src/api';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {searchInputSx, toolbarActionsSx} from 'src/components/tables/tableToolbar.sx';
import {filtersSx} from 'src/pages/EnergyConsumptionReport/components/FiltersBar/FiltersBar.sx';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import {useCan} from 'src/permissions/hooks';
import {PermissionDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';
import {cardSx, emptySx, sectionHintSx} from 'src/pages/AccessControl/AccessControl.sx';
import {IAM_TONE, listRowSx, pillSx, subjectBadgeSx, subjectBandSx} from '../iam.sx';
import {PermissionFormModal} from '../PermissionFormModal';
import {useBusinessId} from 'src/app/hooks';


/**
 * The catalog: every action the platform can check for.
 *
 * Collapsed by subject, because 148 flat rows is a wall rather than a list. The number that makes it
 * navigable is how many roles hand each entry out — nought means nobody in this business can do it,
 * which is the only fact here that ever prompts someone to act.
 *
 * Editing the catalog is a platform-wide capability, so for nearly every viewer none of it renders at
 * all. That is better than disabled controls: a business cannot change the vocabulary its own roles
 * are written in, and showing greyed-out buttons would imply otherwise.
 */
export const PermissionsSection: FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const canManageCatalog = useCan('accessControl.manageCatalog');

    const {data: catalog} = useGetPermissionCatalogQuery();
    // Roles belong to a business, so the list follows the one being worked in.
    const businessId = useBusinessId();
    const {data: roles} = useGetRolesQuery(businessId);

    const [keyword, setKeyword] = useState('');
    const [onlyOrphans, setOnlyOrphans] = useState(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [creatingPermission, setCreatingPermission] = useState(false);

    /** How many roles hand out each permission. */
    const grantedBy = useMemo(() => {
        const counts = new Map<string, number>();
        (roles ?? []).forEach((role) =>
            role.permissions?.forEach((permission) =>
                counts.set(permission.name, (counts.get(permission.name) ?? 0) + 1)
            )
        );
        return counts;
    }, [roles]);

    const bySubject = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        const map = new Map<string, PermissionDTO[]>();
        (catalog ?? [])
            .filter((permission) => !onlyOrphans || (grantedBy.get(permission.name) ?? 0) === 0)
            .filter(
                (permission) =>
                    !needle ||
                    permission.name.toLowerCase().includes(needle) ||
                    (permission.description ?? '').toLowerCase().includes(needle)
            )
            .forEach((permission) => {
                const list = map.get(permission.subject) ?? [];
                list.push(permission);
                map.set(permission.subject, list);
            });
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [catalog, keyword, onlyOrphans, grantedBy]);

    const stats = useMemo(() => {
        const all = catalog ?? [];
        return {
            total: all.length,
            subjects: new Set(all.map((permission) => permission.subject)).size,
            orphans: all.filter((permission) => (grantedBy.get(permission.name) ?? 0) === 0).length,
        };
    }, [catalog, grantedBy]);

    const toggleSubject = (subject: string) => {
        const next = new Set(expanded);
        if (!next.delete(subject)) {
            next.add(subject);
        }
        setExpanded(next);
    };

    return (
        <Box>
            <Typography sx={sectionHintSx}>
                <FormattedMessage
                    id="accessControlPermissionsHint"
                    defaultMessage="Every action the platform can check for. Each entry matches something the backend enforces — you decide which roles hand them out."
                />
                {canManageCatalog && (
                    <>
                        {' '}
                    </>
                )}
            </Typography>


                    <Box sx={{...toolbarActionsSx, mb: 2.5}}>
                        <OutlinedInput
                            sx={searchInputSx}
                            color="secondary"
                            type="text"
                            name="search"
                            value={keyword}
                            onChange={(event) => setKeyword(event.target.value)}
                            placeholder={intl.formatMessage({id: 'search'})}
                            startAdornment={<Search/>}
                        />
                        <Chip
                            sx={onlyOrphans ? filtersSx.chipActive : filtersSx.chip}
                            label={intl.formatMessage({
                                id: 'accessControlGrantedByNobodyFilter',
                                defaultMessage: 'Granted by nobody',
                            })}
                            onClick={() => setOnlyOrphans(!onlyOrphans)}
                        />
                        {canManageCatalog && (
                            <Box sx={{ml: 'auto'}}>
                                <SubmitButton
                                    text="accessControlCreatePermission"
                                    color="brand"
                                    handler={() => setCreatingPermission(true)}
                                />
                            </Box>
                        )}
                    </Box>

                    <Grid container spacing={2} sx={{mb: 2}}>
                        <Grid item xs={6} sm={4} md={2.4}>
                            <KpiCard
                                label={intl.formatMessage({
                                    id: 'accessControlTabPermissions',
                                    defaultMessage: 'Permissions',
                                })}
                                value={String(stats.total)}
                                icon={<KeyOutlined fontSize="small"/>}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                            <KpiCard
                                label={intl.formatMessage({
                                    id: 'accessControlAreas',
                                    defaultMessage: 'Areas',
                                })}
                                value={String(stats.subjects)}
                                icon={<KeyOutlined fontSize="small"/>}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2.4}>
                            <KpiCard
                                label={intl.formatMessage({
                                    id: 'accessControlNobody',
                                    defaultMessage: 'Nobody',
                                })}
                                value={String(stats.orphans)}
                                unit={intl.formatMessage({
                                    id: 'accessControlGrantedByNobodyUnit',
                                    defaultMessage: 'granted by no role',
                                })}
                                icon={<KeyOutlined fontSize="small"/>}
                            />
                        </Grid>
                    </Grid>

                    {bySubject.length === 0 && (
                        <Box sx={cardSx}>
                            <Typography sx={emptySx}>
                                <FormattedMessage
                                    id="accessControlNoPermissions"
                                    defaultMessage="Nothing matches that search"
                                />
                            </Typography>
                        </Box>
                    )}

                    <Stack spacing={2}>
                        {bySubject.map(([subject, group]) => {
                            const open = expanded.has(subject) || !!keyword.trim();
                            const inUse = group.filter(
                                (permission) => (grantedBy.get(permission.name) ?? 0) > 0
                            ).length;
                            return (
                                <Box key={subject} sx={cardSx}>
                                    <Box
                                        sx={{...subjectBandSx, cursor: 'pointer'}}
                                        onClick={() => toggleSubject(subject)}
                                    >
                                        <Box sx={subjectBadgeSx}>
                                            <KeyOutlined fontSize="small"/>
                                        </Box>
                                        <Typography sx={{fontWeight: 600, fontSize: '0.875rem'}}>
                                            {intl.formatMessage({
                                                id: `permissionSubject.${subject}`,
                                                defaultMessage: subject,
                                            })}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            sx={{ml: 'auto'}}
                                        >
                                            <Typography sx={pillSx(IAM_TONE.none)}>
                                                {intl.formatMessage(
                                                    {
                                                        id: 'accessControlNPermissions',
                                                        defaultMessage: '{count} permissions',
                                                    },
                                                    {count: group.length}
                                                )}
                                            </Typography>
                                            <Typography
                                                sx={pillSx(inUse ? IAM_TONE.granted : IAM_TONE.refused)}
                                            >
                                                {intl.formatMessage(
                                                    {
                                                        id: 'accessControlNInUse',
                                                        defaultMessage: '{count} in use',
                                                    },
                                                    {count: inUse}
                                                )}
                                            </Typography>
                                            {open ? <ExpandLess/> : <ExpandMore/>}
                                        </Stack>
                                    </Box>

                                    <Collapse in={open}>
                                        {group.map((permission) => {
                                            const count = grantedBy.get(permission.name) ?? 0;
                                            return (
                                                <Box
                                                    key={permission.name}
                                                    sx={{...listRowSx, cursor: 'pointer'}}
                                                    onClick={() =>
                                                        navigate(
                                                            AppRouteSpace([
                                                                AppRouteEnum.app,
                                                                AppRouteEnum.accessControl,
                                                                AppRouteEnum.permissions,
                                                                permission.name,
                                                            ])
                                                        )
                                                    }
                                                >
                                                    <Box sx={{minWidth: 0, flex: 1}}>
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: '0.9375rem',
                                                                }}
                                                            >
                                                                {intl.formatMessage({
                                                                    id: `permission.${permission.subject}.${permission.action}`,
                                                                    defaultMessage: permission.name,
                                                                })}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.8125rem',
                                                                color: theme.palette.grey[500],
                                                            }}
                                                        >
                                                            {permission.description ?? permission.name}
                                                        </Typography>
                                                    </Box>

                                                    <Tooltip
                                                        title={
                                                            count === 0
                                                                ? intl.formatMessage({
                                                                    id: 'accessControlGrantedByNobodyTooltip',
                                                                    defaultMessage:
                                                                        'No role grants this, so nobody in this business can do it.',
                                                                })
                                                                : ''
                                                        }
                                                    >
                                                        <Typography
                                                            sx={pillSx(
                                                                count ? IAM_TONE.granted : IAM_TONE.refused
                                                            )}
                                                        >
                                                            {count === 0
                                                                ? intl.formatMessage({
                                                                    id: 'accessControlNobody',
                                                                    defaultMessage: 'Nobody',
                                                                })
                                                                : intl.formatMessage(
                                                                    {
                                                                        id: 'accessControlGrantedByRoles',
                                                                        defaultMessage: '{count} roles',
                                                                    },
                                                                    {count}
                                                                )}
                                                        </Typography>
                                                    </Tooltip>
                                                    <ChevronRight
                                                        sx={{color: theme.palette.neutral[400]}}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Stack>

            {creatingPermission && (
                <PermissionFormModal onClose={() => setCreatingPermission(false)}/>
            )}

        </Box>
    );
};
